import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import TextInput from '__components/Inputs/TextInput';
import { KizenTypography } from '__app/typography';
import type { KnowledgeCatalog } from '@kizen/api/knowledge';

const StyledWarning = styled(KizenTypography)`
  color: var(--color-viz-cat-7-primary);
  margin-bottom: 15px;
`;

const StyledConfirmWrapper = styled.div`
  margin-top: 15px;
  text-align: start;
`;

type DeleteCatalogModalProps = {
  catalog: KnowledgeCatalog;
  onConfirm: () => Promise<void>;
  onHide: () => void;
};

export const DeleteCatalogModal: FC<DeleteCatalogModalProps> = ({
  catalog,
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasContent =
    Number(catalog.file_source_count) > 0 ||
    Number(catalog.online_source_count) > 0;
  const nameMatches = confirmName === catalog.name;
  const canConfirm = !submitting && (!hasContent || nameMatches);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }, [canConfirm, onConfirm]);

  return (
    <ConfirmationModal
      heading={t('Delete Catalog')}
      buttonText={t('Delete')}
      disabled={!canConfirm}
      show
      onConfirm={handleConfirm}
      onHide={onHide}
    >
      <StyledWarning>
        {t(
          'This will delete {{fileCount}} file {{fileSources}} and {{onlineCount}} online {{onlineSources}}.',
          {
            fileCount: catalog.file_source_count,
            fileSources:
              Number(catalog.file_source_count) === 1
                ? t('source')
                : t('sources'),
            onlineCount: catalog.online_source_count,
            onlineSources:
              Number(catalog.online_source_count) === 1
                ? t('source')
                : t('sources'),
          }
        )}
      </StyledWarning>
      {hasContent ? (
        <StyledConfirmWrapper>
          <TextInput
            label={t('Type the catalog name to confirm')}
            placeholder={catalog.name}
            value={confirmName}
            onChange={setConfirmName}
            data-qa-id="delete-catalog-confirm-name-input"
          />
        </StyledConfirmWrapper>
      ) : null}
    </ConfirmationModal>
  );
};
